import { useCallback } from 'react';
import { useOpportunityPdfDownload } from '@/entities/opportunity';
import type { WidgetFullModeProps } from '@/entities/widget';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import {
  useCreateBrief,
  useOpportunities,
  useOpportunityHeaderConfig,
  useOpportunitySelection,
} from '../model/hooks';
import type { SocialMediaOpportunitiesWidgetProps } from '../model/social-media-opportunities-widget.types';
import { OpportunitiesListWithContext } from './components/OpportunitiesList';
import { OpportunityDetail } from './components/OpportunityDetail';

/**
 * Top-level widget. dependency injection dep bags (`opportunitiesDeps` +
 * `socialMediaOpportunitiesDeps`) are injected by the config-layer
 * factory at `@/lib/config/agents.config.ts`. Both bags are OPTIONAL on
 * the props type so legacy
 * direct mounts (snapshot tests, dashboard embedding) keep compiling;
 * the wired prod paths always supply them.
 */
export function SocialMediaOpportunitiesWidget(
  props: WidgetFullModeProps<SocialMediaOpportunitiesWidgetProps>,
) {
  const { data, opportunitiesDeps, socialMediaOpportunitiesDeps, readOnly } = props;
  const workspaceId = useCurrentWorkspaceId();

  const { opportunities, isLoading, favoritesOnly, setFavoritesOnly, handleToggleFavorite } =
    useOpportunities({ data, workspaceId, api: opportunitiesDeps });

  const { selectedItem, detailsView, clearPersistedSelection } = useOpportunitySelection({
    opportunities,
    dataItems: data.items,
    // Read-only preview (Library) opens the artifact's primary opportunity
    // detail directly instead of the browsable list, falling back to the first
    // item when none is explicitly flagged. Matches the thumbnail, which also
    // represents that primary opportunity.
    dataSelectedItem: data.selectedItem ?? (readOnly ? data.items[0] : undefined),
  });

  const {
    handleCreateBrief,
    handleClose: handleCloseFromBrief,
    isWaiting,
  } = useCreateBrief(
    selectedItem,
    socialMediaOpportunitiesDeps && {
      useIsAiBusy: socialMediaOpportunitiesDeps.useIsAiBusy,
      sendOrCreateChat: socialMediaOpportunitiesDeps.sendOrCreateChat,
    },
  );

  const handleClose = useCallback(() => {
    clearPersistedSelection();
    handleCloseFromBrief();
  }, [clearPersistedSelection, handleCloseFromBrief]);

  const { canDownload, download, isDownloading } = useOpportunityPdfDownload(
    workspaceId,
    selectedItem,
  );
  const handleDownload = useCallback(() => void download(), [download]);

  const headerConfig = useOpportunityHeaderConfig({
    detailsView,
    handleClose,
    handleDownload,
    handleCreateBrief,
    canDownload,
    isDownloading,
    isWaiting,
    readOnly,
  });

  // Create scrollKey that changes when navigation state changes
  // This ensures scroll resets when switching between list and detail views
  // eslint-disable-next-line i18next/no-literal-string -- scroll restoration key discriminator
  const scrollKey = `social-opportunities-${detailsView ? 'detail' : 'list'}-${
    selectedItem?.opportunityId || ''
  }`;

  const opportunityDetailDeps = socialMediaOpportunitiesDeps && {
    SparkCard: socialMediaOpportunitiesDeps.SparkCard,
    mapAudienceProfileToSpark: socialMediaOpportunitiesDeps.mapAudienceProfileToSpark,
    useStartInterview: socialMediaOpportunitiesDeps.useStartInterview,
  };

  return (
    <SidePanel headerConfig={headerConfig} scrollKey={scrollKey}>
      <div className={`h-full ${detailsView ? 'hidden' : 'block'}`}>
        <OpportunitiesListWithContext
          opportunities={opportunities ?? []}
          isLoading={isLoading}
          favoritesOnly={favoritesOnly}
          onFavoritesFilterToggle={setFavoritesOnly}
          onToggleFavorite={readOnly ? undefined : handleToggleFavorite}
        />
      </div>
      <div className={detailsView ? 'block' : 'hidden'}>
        <OpportunityDetail deps={opportunityDetailDeps} readOnly={readOnly} />
      </div>
    </SidePanel>
  );
}
